import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { paymentAPI, cdNoteAPI, settingsAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate = d => formatDateDMY(d, '—');

const Payments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sourceRoute = useRef(searchParams.get('source') || null);
  const { darkMode, color } = useTheme();
  const acc = color.swatch;

  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = {
    background: darkMode ? '#1E293B' : '#FFFFFF',
    borderRadius: 12,
    border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
    padding: 20,
  };
  const inp = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
    background: darkMode ? '#0F172A' : '#F8FAFC',
    color: darkMode ? '#E2E8F0' : '#1E293B',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };
  const btnPrimary = {
    padding: '8px 18px', background: acc, color: '#fff', border: 'none',
    borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
  };
  const btnSecondary = {
    padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0',
    color: darkMode ? '#CBD5E1' : '#64748B', border: 'none',
    borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
  };

  const { customers, suppliers, refreshCustomers, refreshSuppliers } = useData();

  const [tab, setTab] = useState(searchParams.get('action') === 'create' ? 'record' : 'overview'); // overview, ledger, record, reminders
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [ledgerPartyType, setLedgerPartyType] = useState('customer');
  const [ledgerPartyId, setLedgerPartyId] = useState('');
  const [cdNotes, setCdNotes] = useState([]);
  const [cdLoading, setCdLoading] = useState(false);
  const [cdSaving, setCdSaving] = useState(false);
  const [cdFilter, setCdFilter] = useState('all');
  const [showCdForm, setShowCdForm] = useState(false);
  const [cdType, setCdType] = useState('credit');
  const [cdForm, setCdForm] = useState({
    customer_name: '',
    invoice_id: '',
    reason: '',
    description: '',
    amount: '',
    note_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Cash',
  });
  const [loading, setLoading] = useState(true);

  // Ledger filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [entryFilter, setEntryFilter] = useState('all');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerSending, setLedgerSending] = useState('');

  // Direct payment form
  const [dpPartyType, setDpPartyType] = useState('customer');
  const [dpPartyId, setDpPartyId] = useState('');
  const [dpAmount, setDpAmount] = useState('');
  const [dpDirection, setDpDirection] = useState('in');
  const [dpMode, setDpMode] = useState('Cash');
  const [dpReference, setDpReference] = useState('');
  const [dpNotes, setDpNotes] = useState('');
  const [dpChequeBank, setDpChequeBank] = useState('');
  const [dpChequeNumber, setDpChequeNumber] = useState('');
  const [dpChequeDate, setDpChequeDate] = useState('');
  const [dpDate, setDpDate] = useState(new Date().toISOString().split('T')[0]);
  const [dpSaving, setDpSaving] = useState(false);

  // Edit direct payment date state
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [editingSaving, setEditingSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, remRes] = await Promise.all([
        paymentAPI.summary().catch(() => ({ data: {} })),
        paymentAPI.reminders().catch(() => ({ data: [] })),
      ]);
      setSummary(sumRes.data);
      setReminders(Array.isArray(remRes.data) ? remRes.data : []);
      refreshCustomers();
      refreshSuppliers();
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [refreshCustomers, refreshSuppliers]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'record', 'ledger', 'reminders'].includes(tabParam)) {
      setTab(tabParam);
    }
    if (searchParams.get('notes') === '1') {
      setTab('ledger');
      setShowCdForm(true);
    }
  }, [searchParams]);

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [tab, setSearchParams]);

  const loadLedger = useCallback(async () => {
    if (!ledgerPartyId) { setLedger(null); return; }
    try {
      const res = await paymentAPI.getLedger(ledgerPartyType, ledgerPartyId);
      setLedger(res.data && typeof res.data === 'object' ? res.data : { entries: Array.isArray(res.data) ? res.data : [], balance: 0 });
    } catch (err) { console.error('Ledger load failed:', err); setLedger(null); alert(err.response?.data?.detail || 'Failed to load ledger'); }
  }, [ledgerPartyType, ledgerPartyId]);

  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);

  const loadCreditDebitNotes = useCallback(async () => {
    if (!ledgerPartyId) { setCdNotes([]); return; }
    setCdLoading(true);
    try {
      const params = { party_type: ledgerPartyType, party_id: ledgerPartyId };
      if (cdFilter !== 'all') params.note_type = cdFilter;
      const res = await cdNoteAPI.list(params);
      setCdNotes(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('Credit/debit note load failed:', err);
      setCdNotes([]);
    }
    setCdLoading(false);
  }, [ledgerPartyId, ledgerPartyType, cdFilter]);

  useEffect(() => {
    if (tab === 'ledger') loadCreditDebitNotes();
  }, [tab, loadCreditDebitNotes]);

  const resetCdForm = useCallback((name = '') => {
    setCdForm({
      customer_name: name,
      invoice_id: '',
      reason: '',
      description: '',
      amount: '',
      note_date: new Date().toISOString().split('T')[0],
      payment_mode: 'Cash',
    });
  }, []);

  useEffect(() => {
    const partyPool = ledgerPartyType === 'customer' ? customers : suppliers;
    const selectedParty = partyPool.find(p => String(p.id) === String(ledgerPartyId));
    if (selectedParty?.name && !cdForm.customer_name) {
      setCdForm(f => ({ ...f, customer_name: selectedParty.name }));
    }
  }, [ledgerPartyId, ledgerPartyType, customers, suppliers, cdForm.customer_name]);

  const handleCreateCdNote = async (e) => {
    e.preventDefault();
    if (!ledgerPartyId) return alert('Please select a party first');
    const amt = parseFloat(cdForm.amount || 0);
    if (!amt || amt <= 0) return alert('Please enter a valid amount');
    setCdSaving(true);
    try {
      const partyName = cdForm.customer_name || ledgerPartyList.find(p => String(p.id) === String(ledgerPartyId))?.name || 'Party';
      await cdNoteAPI.create({
        note_type: cdType,
        party_type: ledgerPartyType,
        party_id: ledgerPartyId,
        customer_id: ledgerPartyType === 'customer' ? ledgerPartyId : undefined,
        supplier_id: ledgerPartyType === 'supplier' ? ledgerPartyId : undefined,
        invoice_id: cdForm.invoice_id || null,
        customer_name: partyName,
        reason: cdForm.reason || 'Adjustment',
        description: cdForm.description || null,
        amount: amt,
        subtotal: amt,
        gst_total: 0,
        grand_total: amt,
        note_date: cdForm.note_date || new Date().toISOString().split('T')[0],
        payment_mode: cdForm.payment_mode || 'Cash',
      });
      alert('Credit/Debit note created successfully');
      setShowCdForm(false);
      resetCdForm(cdForm.customer_name);
      await Promise.all([loadCreditDebitNotes(), loadLedger()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create note');
    }
    setCdSaving(false);
  };

  const handleDeleteCdNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await cdNoteAPI.delete(id);
      await Promise.all([loadCreditDebitNotes(), loadLedger()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleDirectPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(dpAmount);
    if (!dpPartyId || isNaN(amount) || amount <= 0) return alert('Select party and enter a valid amount greater than zero');
    setDpSaving(true);
    try {
      await paymentAPI.recordDirect({
        party_type: dpPartyType,
        party_id: dpPartyId,
        amount: amount,
        payment_mode: dpMode,
        direction: dpDirection,
        payment_date: dpDate || undefined,
        reference: dpReference || undefined,
        notes: dpNotes || undefined,
        cheque_bank: dpMode === 'Cheque' ? dpChequeBank || undefined : undefined,
        cheque_number: dpMode === 'Cheque' ? dpChequeNumber || undefined : undefined,
        cheque_date: dpMode === 'Cheque' ? dpChequeDate || undefined : undefined,
      });
      alert('Payment recorded successfully!');
      setDpAmount(''); setDpReference(''); setDpNotes('');
      setDpChequeBank(''); setDpChequeNumber(''); setDpChequeDate('');
      setDpDate(new Date().toISOString().split('T')[0]);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record payment');
    }
    setDpSaving(false);
  };

  const dismissReminder = async (id) => {
    try {
      await paymentAPI.dismissReminder(id);
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (e) { console.error(e); }
  };

  const startEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setEditingDate(entry.entry_date ? entry.entry_date.split('T')[0] : new Date().toISOString().split('T')[0]);
  };

  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEditingDate('');
  };

  const saveEditEntry = async (entryId) => {
    if (!editingDate) return;
    setEditingSaving(true);
    try {
      await paymentAPI.updateDirect(entryId, { payment_date: editingDate });
      setEditingEntryId(null);
      setEditingDate('');
      await loadLedger();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update payment date');
    }
    setEditingSaving(false);
  };

  const partyList = dpPartyType === 'customer' ? customers : suppliers;
  const ledgerPartyList = ledgerPartyType === 'customer' ? customers : suppliers;
  const directionOptions = dpPartyType === 'supplier'
    ? [
        { value: 'out', label: '💸 Payment To Supplier' },
        { value: 'in', label: '↩️ Refund From Supplier' },
      ]
    : [
        { value: 'in', label: '💵 Payment From Customer' },
        { value: 'out', label: '↩️ Refund / Payout To Customer' },
      ];

  // Computed ledger values (matching Parties-style)
  const ledgerEntries = ledger?.entries || [];
  const filteredEntries = ledgerEntries.filter(e => {
    if (entryFilter !== 'all' && e.entry_type !== entryFilter) return false;
    if (dateFrom && e.entry_date < dateFrom) return false;
    if (dateTo && e.entry_date > dateTo) return false;
    if (ledgerSearch) {
      const q = ledgerSearch.toLowerCase();
      if (!(e.description || '').toLowerCase().includes(q) &&
          !(e.payment_mode || '').toLowerCase().includes(q) &&
          !(String(e.reference_id || '')).toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const entriesWithBalance = filteredEntries;

  const totalCredit = filteredEntries.filter(e => e.entry_type === 'payment_in').reduce((s, e) => s + parseFloat(e.amount), 0);
  const totalDebit = filteredEntries.filter(e => e.entry_type === 'payment_out').reduce((s, e) => s + parseFloat(e.amount), 0);

  const sendLedgerVia = useCallback(async (channel) => {
    if (!ledgerPartyId) return;
    setLedgerSending(channel);
    try {
      const selectedParty = ledgerPartyList.find(p => String(p.id) === String(ledgerPartyId));
      const phone = ledgerPartyType === 'customer'
        ? (selectedParty?.mobile || selectedParty?.mobile2 || selectedParty?.mobile3 || '')
        : (selectedParty?.phone || selectedParty?.contact_no || '');
      const recipient = selectedParty?.email || '';

      if (channel === 'whatsapp' && !phone) {
        throw new Error(`${ledgerPartyType === 'customer' ? 'Customer' : 'Supplier'} phone is required to send ledger via WhatsApp`);
      }
      if (channel === 'email' && !recipient) {
        throw new Error(`${ledgerPartyType === 'customer' ? 'Customer' : 'Supplier'} email is required to send ledger via Email`);
      }

      await settingsAPI.sendLedger({
        party_type: ledgerPartyType,
        party_name: selectedParty?.name || 'Party',
        channel,
        entries: entriesWithBalance,
        recipient: recipient || undefined,
        phone: phone || undefined,
      });
      alert(`✅ Ledger sent via ${channel}!`);
    } catch (err) {
      alert(`❌ ${err.response?.data?.detail || err.message || `Failed to send ledger via ${channel}`}`);
    }
    setLedgerSending('');
  }, [entriesWithBalance, ledgerPartyId, ledgerPartyList, ledgerPartyType]);

  const searchRef = useRef(null);

  // Listen for sv-open-modal custom event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'new-payment') setTab('record');
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  // Clean up query params
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action'); next.delete('source');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const inInput = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;
      if (e.key === 'Escape') {
        if (showCdForm) { setShowCdForm(false); return; }
        if (tab === 'record') { setTab('overview'); return; }
        if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); return; }
        return;
      }
      if (inInput) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); setTab('record'); return; }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showCdForm, tab, navigate]);

  const TabBtn = ({ value, label, icon }) => (
    <button onClick={() => setTab(value)}
      style={{
        padding: '8px 16px', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
        background: tab === value ? acc : 'transparent',
        color: tab === value ? '#fff' : (darkMode ? '#94A3B8' : '#64748B'),
        transition: 'all 0.2s',
      }}>{icon} {label}</button>
  );

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div style={{ ...pg, fontSize: 16 }}>Loading payments...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, margin: 0 }}>💳 Payments</h1>
          <p style={{ ...muted, fontSize: 13, margin: '4px 0 0' }}>Track pay-ins, pay-outs, ledger &amp; reminders</p>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          <TabBtn value="overview" label="Overview" icon="📊" />
          <TabBtn value="record" label="Record Payment" icon="💰" />
          <TabBtn value="ledger" label="Ledger" icon="📒" />
          <TabBtn value="reminders" label="Reminders" icon="🔔" />
        </div>

        {/* ─── Overview Tab ─── */}
        {tab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }} className="stagger-list">
              <div style={{ ...card, borderLeft: '4px solid #16A34A' }}>
                <div style={{ ...muted, fontSize: 12, fontWeight: 600 }}>💵 Total Pay-In (Received)</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800, marginTop: 6 }}>₹{fmtNum(summary?.total_pay_in)}</div>
                <div style={{ ...muted, fontSize: 11, marginTop: 3 }}>{summary?.pay_in_count || 0} transactions</div>
              </div>
              <div style={{ ...card, borderLeft: '4px solid #DC2626' }}>
                <div style={{ ...muted, fontSize: 12, fontWeight: 600 }}>💸 Total Pay-Out (Sent)</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800, marginTop: 6 }}>₹{fmtNum(summary?.total_pay_out)}</div>
                <div style={{ ...muted, fontSize: 11, marginTop: 3 }}>{summary?.pay_out_count || 0} transactions</div>
              </div>
              <div style={{ ...card, borderLeft: `4px solid ${acc}` }}>
                <div style={{ ...muted, fontSize: 12, fontWeight: 600 }}>📊 Net Balance</div>
                <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: (summary?.total_pay_in || 0) >= (summary?.total_pay_out || 0) ? '#16A34A' : '#DC2626' }}>
                  ₹{fmtNum((summary?.total_pay_in || 0) - (summary?.total_pay_out || 0))}
                </div>
              </div>
              <div style={{ ...card, borderLeft: '4px solid #D97706' }}>
                <div style={{ ...muted, fontSize: 12, fontWeight: 600 }}>⏳ Pending Amount</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800, marginTop: 6 }}>₹{fmtNum(summary?.pending_amount)}</div>
                <div style={{ ...muted, fontSize: 11, marginTop: 3 }}>Unpaid invoices</div>
              </div>
            </div>

            {/* Active Reminders Preview */}
            {reminders.length > 0 && (
              <div style={card}>
                <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🔔 Active Reminders ({reminders.length})</div>
                {reminders.slice(0, 5).map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                    <div>
                      <span style={{ ...pg, fontSize: 13, fontWeight: 600 }}>
                        {r.payment_mode === 'Cheque' ? '🏦' : '🏢'} {r.payment_mode} — ₹{fmtNum(r.amount)}
                      </span>
                      <div style={{ ...muted, fontSize: 11 }}>
                        {r.payment_mode === 'Cheque' ? `Bank: ${r.cheque_bank || '—'} | Cheque #${r.cheque_number || '—'} | Date: ${fmtDate(r.cheque_date)}` :
                          `Company: ${r.finance_company || '—'} | EMI: ₹${fmtNum(r.finance_emi_amount)} | Due: ${fmtDate(r.finance_emi_date)}`}
                      </div>
                    </div>
                    <button onClick={() => dismissReminder(r.id)} style={btnSecondary}>Dismiss</button>
                  </div>
                ))}
                {reminders.length > 5 && (
                  <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <button onClick={() => setTab('reminders')} style={{ ...btnSecondary, fontSize: 12 }}>View all {reminders.length} reminders →</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── Record Payment Tab ─── */}
        {tab === 'record' && (
          <div style={{ ...card, maxWidth: 560 }}>
            <div style={{ ...pg, fontWeight: 700, fontSize: 16, marginBottom: 16 }}>💰 Record Direct Payment</div>
            <form onSubmit={handleDirectPayment} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div>
                  <label style={lbl}>Party Type</label>
                  <select style={inp} value={dpPartyType} onChange={e => {
                    const nextPartyType = e.target.value;
                    setDpPartyType(nextPartyType);
                    setDpPartyId('');
                    setDpDirection(nextPartyType === 'supplier' ? 'out' : 'in');
                  }}>
                    <option value="customer">Customer</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Direction</label>
                  <select style={inp} value={dpDirection} onChange={e => setDpDirection(e.target.value)}>
                    {directionOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Select {dpPartyType === 'customer' ? 'Customer' : 'Supplier'}</label>
                <select style={inp} value={dpPartyId} onChange={e => setDpPartyId(e.target.value)}>
                  <option value="">— Select —</option>
                  {partyList.map(p => (
                    <option key={p.id} value={p.id}>{p.name} {p.phone ? `(${p.phone})` : ''}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div>
                  <label style={lbl}>Amount (₹)</label>
                  <input type="number" min="0" step="0.01" style={inp} value={dpAmount} onChange={e => setDpAmount(e.target.value)} placeholder="0.00" required />
                </div>
                <div>
                  <label style={lbl}>Payment Mode</label>
                  <select style={inp} value={dpMode} onChange={e => setDpMode(e.target.value)}>
                    {['Cash', 'UPI', 'Card', 'Net Banking', 'Cheque', 'Finance', 'Other'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Payment Date</label>
                <StrictDateInput style={inp} value={dpDate} onChange={e => setDpDate(e.target.value)} required />
                {dpDate !== new Date().toISOString().split('T')[0] && (
                  <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 3 }}>⚠️ Date is not today — entry will be posted on {formatDateDMY(dpDate)}</div>
                )}
              </div>
              {dpMode === 'Cheque' && (
                <div style={{ padding: 12, background: darkMode ? '#0F172A' : '#FFF7ED', borderRadius: 8, border: `1px solid ${darkMode ? '#78350F' : '#FED7AA'}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>🏦 Cheque Details</div>
                  <input style={inp} value={dpChequeBank} onChange={e => setDpChequeBank(e.target.value)} placeholder="Issuing Bank Name" />
                  <input style={inp} value={dpChequeNumber} onChange={e => setDpChequeNumber(e.target.value)} placeholder="Cheque Number" />
                  <div>
                    <label style={lbl}>Cheque Date</label>
                    <StrictDateInput style={inp} value={dpChequeDate} onChange={e => setDpChequeDate(e.target.value)} />
                  </div>
                </div>
              )}
              <div>
                <label style={lbl}>Reference (optional)</label>
                <input style={inp} value={dpReference} onChange={e => setDpReference(e.target.value)} placeholder="Transaction ID, receipt no, etc." />
              </div>
              <div>
                <label style={lbl}>Notes (optional)</label>
                <textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={dpNotes} onChange={e => setDpNotes(e.target.value)} placeholder="Remarks..." />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={dpSaving} style={{ ...btnPrimary, opacity: dpSaving ? 0.6 : 1 }}>
                  {dpSaving ? '⏳ Saving...' : '✅ Record Payment'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ─── Ledger Tab ─── */}
        {tab === 'ledger' && (
          <div>
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={lbl}>Party Type</label>
                  <select style={inp} value={ledgerPartyType} onChange={e => { setLedgerPartyType(e.target.value); setLedgerPartyId(''); setLedger(null); setCdNotes([]); resetCdForm(''); }}>
                    <option value="customer">Customer</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={lbl}>Select {ledgerPartyType === 'customer' ? 'Customer' : 'Supplier'}</label>
                  <select style={inp} value={ledgerPartyId} onChange={e => { setLedgerPartyId(e.target.value); setShowCdForm(false); }}>
                    <option value="">— Select —</option>
                    {ledgerPartyList.map(p => (
                      <option key={p.id} value={p.id}>{p.name} {p.phone ? `(${p.phone})` : ''}</option>
                    ))}
                  </select>
                </div>
                <button onClick={loadLedger} style={btnPrimary}>🔍 Load Ledger</button>
              </div>
            </div>

            {ledgerPartyId && (
              <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ ...pg, fontWeight: 700, fontSize: 14 }}>📝 Credit / Debit Notes (Merged into Ledger)</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['all', 'credit', 'debit'].map(f => (
                        <button key={f} onClick={() => setCdFilter(f)}
                          style={{
                            padding: '6px 12px', borderRadius: 8,
                            border: `1px solid ${cdFilter === f ? acc : (darkMode ? '#334155' : '#E2E8F0')}`,
                            background: cdFilter === f ? acc : 'transparent',
                            color: cdFilter === f ? '#fff' : (darkMode ? '#CBD5E1' : '#64748B'),
                            fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize'
                          }}>
                          {f === 'all' ? 'All' : `${f} Notes`}
                        </button>
                      ))}
                      <button onClick={() => {
                        const selectedParty = ledgerPartyList.find(p => String(p.id) === String(ledgerPartyId));
                        if (!showCdForm) resetCdForm(selectedParty?.name || '');
                        setShowCdForm(s => !s);
                      }} style={btnPrimary}>
                        {showCdForm ? 'Cancel' : '+ Add Note'}
                      </button>
                    </div>
                  </div>

                {showCdForm && (
                  <form onSubmit={handleCreateCdNote} style={{ marginBottom: 14, padding: 12, borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => setCdType('credit')} style={cdType === 'credit' ? btnPrimary : btnSecondary}>Credit Note</button>
                      <button type="button" onClick={() => setCdType('debit')} style={cdType === 'debit' ? btnPrimary : btnSecondary}>Debit Note</button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={lbl}>Party Name</label>
                        <input style={inp} value={cdForm.customer_name} onChange={e => setCdForm(f => ({ ...f, customer_name: e.target.value }))} required />
                      </div>
                      <div>
                        <label style={lbl}>Invoice ID (optional)</label>
                        <input style={inp} value={cdForm.invoice_id} onChange={e => setCdForm(f => ({ ...f, invoice_id: e.target.value }))} placeholder="Invoice UUID" />
                      </div>
                      <div>
                        <label style={lbl}>Reason</label>
                        <input style={inp} value={cdForm.reason} onChange={e => setCdForm(f => ({ ...f, reason: e.target.value }))} placeholder="Return / Adjustment" required />
                      </div>
                      <div>
                        <label style={lbl}>Amount (₹)</label>
                        <input type="number" min="0.01" step="0.01" style={inp} value={cdForm.amount} onChange={e => setCdForm(f => ({ ...f, amount: e.target.value }))} required />
                      </div>
                      <div>
                        <label style={lbl}>Date</label>
                        <StrictDateInput style={inp} value={cdForm.note_date} onChange={e => setCdForm(f => ({ ...f, note_date: e.target.value }))} required />
                      </div>
                      <div>
                        <label style={lbl}>Payment Mode</label>
                        <select style={inp} value={cdForm.payment_mode} onChange={e => setCdForm(f => ({ ...f, payment_mode: e.target.value }))}>
                          {['Cash','Card','UPI','Bank Transfer','Cheque','Credit','Other'].map(m => <option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={lbl}>Description (optional)</label>
                        <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={cdForm.description} onChange={e => setCdForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional details…" />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="submit" disabled={cdSaving} style={{ ...btnPrimary, opacity: cdSaving ? 0.7 : 1 }}>{cdSaving ? 'Saving...' : 'Create Note'}</button>
                    </div>
                  </form>
                )}

                {cdLoading ? (
                  <div style={{ ...muted, fontSize: 13 }}>Loading notes...</div>
                ) : cdNotes.length === 0 ? (
                  <div style={{ ...muted, fontSize: 13 }}>No credit/debit notes for this party.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                          {['Note No', 'Type', 'Date', 'Reason', 'Total', 'Action'].map(h => (
                            <th key={h} style={{ ...muted, fontWeight: 700, fontSize: 11, textAlign: 'left', padding: '8px 6px', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cdNotes.map(note => (
                          <tr key={note.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                            <td style={{ ...pg, padding: '8px 6px', fontWeight: 700 }}>{note.note_number || '—'}</td>
                            <td style={{ padding: '8px 6px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                                background: note.note_type === 'credit' ? '#DCFCE7' : '#FEE2E2',
                                color: note.note_type === 'credit' ? '#166534' : '#991B1B' }}>
                                {(note.note_type || '—').toUpperCase()}
                              </span>
                            </td>
                            <td style={{ ...pg, padding: '8px 6px' }}>{fmtDate(note.note_date)}</td>
                            <td style={{ ...pg, padding: '8px 6px' }}>{note.reason || '—'}{note.description ? <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8' }}>{note.description}</div> : null}</td>
                            <td style={{ ...pg, padding: '8px 6px', fontWeight: 700 }}>₹{fmtNum(note.grand_total)}</td>
                            <td style={{ padding: '8px 6px' }}>
                              <button onClick={() => handleDeleteCdNote(note.id)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11, color: '#EF4444' }}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {ledgerPartyId && ledger && (
              <>
                {/* Summary cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
                  {[
                    ['Total Debit', `₹${fmtNum(totalDebit)}`, '#DC2626'],
                    ['Total Credit', `₹${fmtNum(totalCredit)}`, '#16A34A'],
                    ['Net Balance', `₹${fmtNum(Math.abs(ledger?.balance ?? 0))} ${parseFloat(ledger?.balance ?? 0) >= 0 ? 'Dr' : 'Cr'}`, parseFloat(ledger?.balance ?? 0) >= 0 ? '#DC2626' : '#16A34A'],
                    ['Entries', filteredEntries.length, acc],
                  ].map(([label, val, clr]) => (
                    <div key={label} style={{ background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: clr }}>{val}</div>
                      <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, padding: '10px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  <StrictDateInput value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }} />
                  <span style={{ alignSelf: 'center', ...muted, fontSize: 12 }}>to</span>
                  <StrictDateInput value={dateTo} onChange={e => setDateTo(e.target.value)}
                    style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }} />
                  <select value={entryFilter} onChange={e => setEntryFilter(e.target.value)}
                    style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }}>
                    <option value="all">All Entries</option>
                    <option value="payment_in">Credits Only</option>
                    <option value="payment_out">Debits Only</option>
                  </select>
                  <input value={ledgerSearch} onChange={e => setLedgerSearch(e.target.value)}
                    placeholder="Search description, mode…"
                    style={{ ...inp, flex: '1 1 140px', fontSize: 13, padding: '7px 10px' }} />
                  {(dateFrom || dateTo || entryFilter !== 'all' || ledgerSearch) && (
                    <button onClick={() => { setDateFrom(''); setDateTo(''); setEntryFilter('all'); setLedgerSearch(''); }}
                      style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, fontSize: 12, cursor: 'pointer', ...muted }}>
                      &times; Clear
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                  <button
                    onClick={() => sendLedgerVia('whatsapp')}
                    disabled={entriesWithBalance.length === 0 || ledgerSending === 'whatsapp'}
                    style={{ padding: '7px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'whatsapp') ? 0.6 : 1 }}>
                    {ledgerSending === 'whatsapp' ? '⏳' : '💬'} WhatsApp
                  </button>
                  <button
                    onClick={() => sendLedgerVia('telegram')}
                    disabled={entriesWithBalance.length === 0 || ledgerSending === 'telegram'}
                    style={{ padding: '7px 14px', background: '#0088cc', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'telegram') ? 0.6 : 1 }}>
                    {ledgerSending === 'telegram' ? '⏳' : '✈️'} Telegram
                  </button>
                  <button
                    onClick={() => sendLedgerVia('email')}
                    disabled={entriesWithBalance.length === 0 || ledgerSending === 'email'}
                    style={{ padding: '7px 14px', background: '#EA4335', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'email') ? 0.6 : 1 }}>
                    {ledgerSending === 'email' ? '⏳' : '📧'} Email
                  </button>
                </div>

                {filteredEntries.length === 0
                  ? <div style={{ ...card, textAlign: 'center', padding: 40, ...muted }}>No ledger entries{(dateFrom || dateTo || entryFilter !== 'all') ? ' matching filters' : ''}</div>
                  : <div style={card}>
                      <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12 }}>📒 Ledger Entries</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                          <thead>
                            <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', position: 'sticky', top: 0 }}>
                              {['Date','Description','Mode','Reference','Debit','Credit','Balance',''].map(h => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: ['Debit','Credit','Balance'].includes(h) ? 'right' : 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {entriesWithBalance.map((e, i) => (
                              <tr key={e.id || i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, background: i % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...muted, whiteSpace: 'nowrap' }}>
                                  {editingEntryId === e.id && e.reference_type === 'direct_payment' ? (
                                    <StrictDateInput style={{ ...inp, padding: '4px 8px', fontSize: 12, width: 130 }}
                                      value={editingDate} onChange={ev => setEditingDate(ev.target.value)} />
                                  ) : fmtDate(e.entry_date || e.created_at)}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...pg, maxWidth: 200 }}>
                                  <div>{e.description || '—'}</div>
                                  {e.reference_type && <div style={{ fontSize: 10, ...muted }}>{e.reference_type.replace(/_/g,' ')}</div>}
                                  {e.cheque_date && <div style={{ fontSize: 10, color: '#f59e0b' }}>Cheque: {fmtDate(e.cheque_date)}</div>}
                                  {e.finance_emi_date && <div style={{ fontSize: 10, color: '#8b5cf6' }}>EMI: {fmtDate(e.finance_emi_date)}</div>}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...muted, whiteSpace: 'nowrap' }}>{e.payment_mode || '—'}</td>
                                <td style={{ padding: '8px 10px', fontSize: 11, fontFamily: 'monospace', ...muted }}>{String(e.reference_id || '').slice(0,8) || '—'}</td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: e.entry_type === 'payment_out' ? '#DC2626' : 'transparent' }}>
                                  {e.entry_type === 'payment_out' ? `₹${fmtNum(e.amount)}` : ''}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: e.entry_type === 'payment_in' ? '#16A34A' : 'transparent' }}>
                                  {e.entry_type === 'payment_in' ? `₹${fmtNum(e.amount)}` : ''}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 800, textAlign: 'right', color: (e.running_balance || 0) >= 0 ? '#DC2626' : '#16A34A', whiteSpace: 'nowrap' }}>
                                  ₹{fmtNum(Math.abs(e.running_balance || 0))}
                                  <span style={{ fontSize: 9, marginLeft: 3, ...muted }}>{(e.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}</span>
                                </td>
                                <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                  {e.reference_type === 'direct_payment' && (
                                    editingEntryId === e.id ? (
                                      <span style={{ display: 'inline-flex', gap: 4 }}>
                                        <button onClick={() => saveEditEntry(e.id)} disabled={editingSaving}
                                          style={{ padding: '3px 8px', fontSize: 11, borderRadius: 6, border: 'none', background: '#16A34A', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                                          {editingSaving ? '…' : '✓'}
                                        </button>
                                        <button onClick={cancelEditEntry}
                                          style={{ padding: '3px 8px', fontSize: 11, borderRadius: 6, border: 'none', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', cursor: 'pointer' }}>
                                          ✕
                                        </button>
                                      </span>
                                    ) : (
                                      <button onClick={() => startEditEntry(e)}
                                        style={{ padding: '3px 8px', fontSize: 11, borderRadius: 6, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, background: 'transparent', color: darkMode ? '#94A3B8' : '#64748B', cursor: 'pointer' }}>
                                        ✏️
                                      </button>
                                    )
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderTop: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                              <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, ...pg, fontSize: 12 }}>Total ({filteredEntries.length} entries)</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: '#DC2626', fontSize: 13 }}>₹{fmtNum(totalDebit)}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: '#16A34A', fontSize: 13 }}>₹{fmtNum(totalCredit)}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, fontSize: 13, color: (totalDebit - totalCredit) >= 0 ? '#DC2626' : '#16A34A' }}>₹{fmtNum(Math.abs(totalDebit - totalCredit))} {(totalDebit - totalCredit) >= 0 ? 'Dr' : 'Cr'}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                }
              </>
            )}

            {!ledgerPartyId && (
              <div style={{ ...card, textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>👆</div>
                <div style={muted}>Select a customer or supplier to view their ledger</div>
              </div>
            )}
          </div>
        )}

        {/* ─── Reminders Tab ─── */}
        {tab === 'reminders' && (
          <div>
            {reminders.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reminders.map(r => (
                  <div key={r.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 20 }}>{r.payment_mode === 'Cheque' ? '🏦' : '🏢'}</span>
                        <span style={{ ...pg, fontWeight: 700, fontSize: 14 }}>{r.payment_mode} Payment — ₹{fmtNum(r.amount)}</span>
                      </div>
                      {r.payment_mode === 'Cheque' && (
                        <div style={{ ...muted, fontSize: 12 }}>
                          Bank: {r.cheque_bank || '—'} &nbsp;|&nbsp; Cheque #{r.cheque_number || '—'} &nbsp;|&nbsp; Date: {fmtDate(r.cheque_date)}
                        </div>
                      )}
                      {r.payment_mode === 'Finance' && (
                        <div style={{ ...muted, fontSize: 12 }}>
                          Company: {r.finance_company || '—'} &nbsp;|&nbsp; Scheme: {r.finance_scheme || '—'} &nbsp;|&nbsp;
                          EMI: ₹{fmtNum(r.finance_emi_amount)}/mo &nbsp;|&nbsp; Due: {fmtDate(r.finance_emi_date)} &nbsp;|&nbsp;
                          Fee: ₹{fmtNum(r.finance_processing_fee)}
                        </div>
                      )}
                      <div style={{ ...muted, fontSize: 11, marginTop: 4 }}>Created: {fmtDate(r.created_at)}</div>
                    </div>
                    <button onClick={() => dismissReminder(r.id)} style={{ ...btnSecondary, whiteSpace: 'nowrap' }}>✓ Dismiss</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...card, textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔔</div>
                <div style={{ ...pg, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>No Active Reminders</div>
                <div style={muted}>Cheque and finance payment reminders will appear here</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Payments;
